'use client';
import { type VerifiedStatistic } from '@/data/verified-statistics';
interface SourceCitationProps {
  stat: VerifiedStatistic;
  compact?: boolean;
}
export function SourceCitation({ stat, compact = false }: SourceCitationProps) {
  const year = new Date(stat.publicationDate).getFullYear();
  const verifiedDate = new Date(stat.lastVerified).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  if (compact) {
    return (
      <span className="text-xs text-gray-500 dark:text-gray-400 italic">
        (Source:{' '}
        <a
          href={stat.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-[#4361ee] transition-colors"
        >
          {stat.source}, {year}
        </a>
        )
        {stat.isProjection && (
          <span className="text-amber-500 ml-1">[Projection]</span>
        )}
      </span>
    );
  }
  return (
    <div className="mt-2 p-3 rounded-lg bg-white/[0.03] border border-white/[0.06] text-sm">
      <div className="flex items-start gap-2">
        <div
          className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
          style={{
            backgroundColor:
              stat.confidence === 'high'
                ? '#22c55e'
                : stat.confidence === 'medium'
                ? '#eab308'
                : '#ef4444',
          }}
          title={`Confidence: ${stat.confidence}`}
        />
        <div>
          <p className="text-gray-300">{stat.claim}</p>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Source:{' '}
            <a
              href={stat.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#4361ee] hover:underline"
            >
              {stat.source}
            </a>{' '}
            ({year})
          </p>
          {stat.isProjection && (
            <p className="text-amber-500/80 text-xs mt-1">
              ⚠ This is a projection, not a guaranteed outcome.
            </p>
          )}
          {stat.context && (
            <p className="text-gray-600 dark:text-gray-400 text-xs mt-1 italic">{stat.context}</p>
          )}
          <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">
            Last verified: {verifiedDate}
          </p>
        </div>
      </div>
    </div>
  );
}
export function SourceCitationInline({
  source,
  url,
  year,
}: {
  source: string;
  url: string;
  year: number;
}) {
  return (
    <sup>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#4361ee]/70 hover:text-[#4361ee] text-[10px] no-underline hover:underline ml-0.5"
        title={`Source: ${source}, ${year}`}
      >
        [{source}, {year}]
      </a>
    </sup>
  );
}
